import { FormikErrors } from 'formik';

import { NodeViewModel } from '@/docker/models/node';
import { useCurrentUser } from '@/react/hooks/useUser';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { BoxSelector } from '@@/BoxSelector';
import { FormControl } from '@@/form-components/FormControl';
import { FormSection } from '@@/form-components/FormSection';
import { Input } from '@@/form-components/Input';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';

import { useNetworks } from '../../queries/useNetworks';
import { useNodes } from '../../../proxy/queries/nodes/useNodes';
import { MacvlanNodesSelector } from '../MacvlanNodesSelector/MacvlanNodesSelector';

import { getOptions } from './options';
import { MacvlanFormValues, MacvlanScope } from './types';

interface Props {
  values: MacvlanFormValues;
  onChange(values: MacvlanFormValues): void;
  errors?: FormikErrors<MacvlanFormValues>;
  environmentId: EnvironmentId;
  isAgentProxy: boolean;
  isSwarmMode: boolean;
  apiVersion: number;
}

export function MacvlanFormSection({
  values,
  onChange,
  errors,
  environmentId,
  isAgentProxy,
  isSwarmMode,
  apiVersion,
}: Props) {
  const { isPureAdmin } = useCurrentUser();

  const nodesQuery = useNodes(environmentId);
  const nodes = nodesQuery.data?.map((n) => new NodeViewModel(n));

  const networksQuery = useNetworks(
    environmentId,
    { local: true, swarm: isSwarmMode },
    {
      select: (networks) => networks.filter((n) => n.ConfigOnly === true),
    }
  );
  const availableNetworks = networksQuery.data ?? [];

  const scopeOptions = getOptions(availableNetworks.length > 0);

  return (
    <FormSection title="Macvlan configuration">
      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue">
            To create a MACVLAN network you need to create a configuration, then
            create the network from this configuration.
          </TextTip>
        </div>
      </div>

      <BoxSelector<MacvlanScope>
        slim
        options={scopeOptions}
        value={values.scope}
        onChange={(scope) => onChange({ ...values, scope })}
        radioName="macvlan-scope"
      />

      {values.scope === 'local' && (
        <>
          <FormControl
            inputId="macvlan-network-card"
            label="Parent network card"
            errors={errors?.parentNetworkCard}
            required
          >
            <Input
              id="macvlan-network-card"
              data-cy="macvlan-network-card-input"
              value={values.parentNetworkCard}
              onChange={(e) =>
                onChange({ ...values, parentNetworkCard: e.target.value })
              }
              placeholder="e.g. eth0 or ens160 ..."
            />
          </FormControl>

          {isAgentProxy && isSwarmMode && (
            <MacvlanNodesSelector
              dataset={nodes}
              isIpColumnVisible={apiVersion >= 1.25}
              haveAccessToNode={isPureAdmin}
              value={values.selectedNodes}
              onChange={(selectedNodes: Array<NodeViewModel>) =>
                onChange({ ...values, selectedNodes })
              }
            />
          )}
        </>
      )}

      {values.scope === 'swarm' && (
        <FormControl
          inputId="macvlan-config-network"
          label="Configuration"
          errors={errors?.selectedNetworkConfig}
          required
        >
          <PortainerSelect
            inputId="macvlan-config-network"
            data-cy="macvlan-config-network-selector"
            value={values.selectedNetworkConfig}
            onChange={(network) =>
              onChange({ ...values, selectedNetworkConfig: network ?? null })
            }
            options={availableNetworks.map((n) => ({
              label: n.Name,
              value: n,
            }))}
            placeholder="Select a network"
            isLoading={networksQuery.isLoading}
          />
        </FormControl>
      )}
    </FormSection>
  );
}
